import React, {useEffect, useState} from 'react';
import {Box, InputAdornment, Pagination, TextField, Typography} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import {useDispatch, useSelector} from 'react-redux';
import {errorToast, getGarageReport, getGarageReportFilters} from '../actions';
import {exportGarageReportApi} from '../api/garages.service';
import CustomButton from '../components/CustomButton';
import GarageReportTable from '../components/CustomTables/GarageReportTable';
import Select from '../components/Select';
import useFilters from '../utils/useFilters';

/**
 * Reports > Garages: all customer garage data. The project and garage dropdowns only list
 * projects/garages that actually have garage data (served by /customer-garages/report/filters),
 * which is why the generic <Filters /> component is not used here.
 */
const GarageReportPage = () => {
  const dispatch = useDispatch();
  const {
    garageReport,
    garageReportPage,
    garageReportTotalPages,
    garageReportLoading,
    garageReportBuildings,
    garageReportGarages,
    garageReportFiltersLoading,
  } = useSelector(s => s.garages);
  const filters = useFilters(val => dispatch(getGarageReport(val)));
  const [exporting, setExporting] = useState(false);

  // File name reflects the active filters: garages_<project>_<garage>_<date>.xlsx
  const exportFileName = () => {
    const slug = v =>
      (v || '')
        .toString()
        .trim()
        .replace(/[^\p{L}\p{N}]+/gu, '-')
        .replace(/^-|-$/g, '');
    const project = garageReportBuildings.find(b => b.id === filters.building);
    const garage = garageReportGarages.find(g => g.id === filters.garage);
    const date = new Date().toISOString().slice(0, 10);
    return ['garages', slug(project?.name), slug(garage?.name), date]
      .filter(Boolean)
      .join('_');
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const {page, size, ...params} = filters.filters;
      const response = await exportGarageReportApi(params, 'xlsx');
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportFileName()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      dispatch(errorToast(e?.message || 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    dispatch(getGarageReport());
  }, []);

  // Garage options follow the selected project.
  useEffect(() => {
    dispatch(getGarageReportFilters(filters.building || null));
  }, [filters.building]);

  const onPageChange = (e, value) => {
    dispatch(getGarageReport({page: value, ...filters.filters}));
  };

  const hasData =
    garageReportBuildings.length > 0 || Object.keys(filters.filters).length > 0;

  return (
    <>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        mb="1.5rem"
        gap="1rem"
        sx={{
          justifyContent: {xs: 'flex-start', sm: 'flex-end'},
          px: {xs: '1rem', sm: 0},
          '& > *': {maxWidth: '100%'},
        }}>
        <TextField
          sx={{width: {xs: '100%', sm: 'auto'}}}
          label="Search"
          placeholder="Customer, plates, site code, garage"
          value={filters.searchValue}
          onChange={filters.onSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Select
          items={garageReportBuildings}
          value={filters.building}
          onChangeValue={filters.onChangeBuilding}
          onClear={filters.clearBuilding}
          label="Filter by project"
          disabled={garageReportFiltersLoading && garageReportBuildings.length === 0}
          width={{xs: '100%', sm: '230px'}}
        />
        <Select
          items={garageReportGarages}
          value={filters.garage}
          onChangeValue={filters.onChangeGarage}
          onClear={filters.clearGarage}
          label="Filter by garage"
          disabled={garageReportGarages.length === 0}
          width={{xs: '100%', sm: '230px'}}
        />
        <CustomButton
          onClick={onExport}
          loading={exporting}
          disabled={garageReport.length === 0}
          startIcon={<DownloadIcon />}
          sx={{width: {xs: '100%', sm: 'auto'}}}>
          Export Excel
        </CustomButton>
      </Box>
      {hasData || garageReportLoading ? (
        <GarageReportTable rows={garageReport} loading={garageReportLoading} />
      ) : (
        <Box mt="2rem" textAlign="center">
          <Typography color="textSecondary">
            No garage data yet. Add it from a customer's profile.
          </Typography>
        </Box>
      )}
      {garageReportTotalPages > 1 && (
        <Box mt="1rem" display="flex" justifyContent="center">
          <Pagination
            count={garageReportTotalPages}
            page={garageReportPage}
            onChange={onPageChange}
          />
        </Box>
      )}
    </>
  );
};

export default GarageReportPage;
